import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useUIDSeed } from 'react-uid';

import CloseIcon from 'mdi-react/CloseIcon';

import { Grow, Position, Width } from './types';

import * as S from './styles';

type OwnProps = {
  children: React.ReactNode;
  toggleElement: React.ReactElement;
  toggleElementStyles?: {};
  toggleElementActiveStyles?: {};
  position?: Position;
  grow?: Grow;
  width?: Width;
  showCloseButton?: boolean;
  closeOnClick?: boolean;
};

const Menu = ({
  children,
  toggleElement,
  toggleElementStyles,
  toggleElementActiveStyles,
  position = 'bottom',
  grow = 'right',
  width = 'auto',
  showCloseButton = false,
  closeOnClick = false,
}: OwnProps) => {
  const [visible, setVisibilityState] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Generate unique UIDs to use as HTML ids
  const getId = useUIDSeed();
  const dropdownId = getId('menu-dropdown');

  const toggleMenu = useCallback(() => {
    setVisibilityState(!visible);
  }, [visible]);

  const handleClick = useCallback(
    e => {
      if (closeOnClick || !menuRef.current?.contains(e.target)) {
        setVisibilityState(false);
      }
    },
    [closeOnClick, setVisibilityState, menuRef]
  );

  useEffect(() => {
    if (visible) {
      document.addEventListener('click', handleClick);
    }

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [visible, handleClick]);

  if (!toggleElement) return null;

  const styles = {
    ...toggleElementStyles,
    ...(visible && toggleElementActiveStyles),
  };

  return (
    <S.Menu ref={menuRef}>
      <toggleElement.type
        style={styles}
        onClick={toggleMenu}
        {...toggleElement.props}
        aria-expanded={visible}
        aria-haspopup="true"
        aria-controls={dropdownId}
        data-test="MenuToggleElement"
      />

      {visible && (
        <S.MenuDropdown
          role="menu"
          id={dropdownId}
          width={width}
          position={position}
          grow={grow}
        >
          {showCloseButton && (
            <S.CloseButton onClick={toggleMenu}>
              <CloseIcon />
            </S.CloseButton>
          )}
          {children}
        </S.MenuDropdown>
      )}
    </S.Menu>
  );
};

export default Menu;
